import React, { useMemo } from 'react'
import { Layout } from 'app/theme/layout'
import { useParams, Redirect } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useQuery } from 'jsonapi-react'
import pluralize from 'pluralize'
import ErrorPage from 'app/pages/ErrorPage'
import NotFoundPage from 'app/pages/NotFoundPage'
import { WhyWorkWithUs, CateringForAnyEvent, HowItWorks, HowItWorksLarge } from 'app/theme/sections'
import { TagList, FeaturedClients } from 'app/theme/sections'
import ImageHero from 'app/theme/ImageHero'
import NavbarFixed from 'app/theme/NavbarFixed'
import Calculator from 'app/theme/calculator'
import CatererCarousel from 'app/theme/CatererCarousel'
import nachos from 'assets/img/nachos.jpg'
import howItWorksLarge from 'assets/img/how-it-works-large.jpg'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import styles from './CityTagPage.module.css'
import { getResizedPhotoUrl } from 'app/utils/photos'
import LazyImage from 'app/theme/LazyImage'
import ImageSection from '../theme/ImageSection'
import {
  shouldShowExtraSections,
  getCustomText,
  getFaqTexts,
  findImage,
  getSectionFontColors,
  getPrecedingArticle
} from '../utils/cityTagContent'

// TODO: move this to a more global place
pluralize.addSingularRule(/smoothies$/i, 'smoothie')
pluralize.addSingularRule(/cookies$/i, 'cookie')

export default function CityTagPageContainer() {
  const params = useParams()

  const cityQuery = useQuery(['places', {
    filter: {
      slug: params.citySlug,
    },
    fields: {
      cities: ['id', 'name']
    },
    page: {
      limit: 1,
    }
  }])

  const tagQuery = useQuery(['tags', {
    filter: {
      slug: params.tagSlug,
      visible: JSON.stringify({
        city_cuisine_catering: {
          visible: true,
        }
      })
    },
    include: [
      'photos',
    ],
    fields: {
      tags: ['id', 'name', 'slug', 'description', 'display_rules'],
      photos: ['path', 'uuid', 'extension', 'type']
    },
    page: {
      limit: 1,
    }
  }])

  const pageQuery = useQuery(['pages', {
    sort: '-priority',
    merge: true,
    expand_content: true,
    filter: {
      type: 'CateringTag',
      city: params.citySlug,
      tag: params.tagSlug,
    },
  }])

  if (cityQuery.isLoading || tagQuery.isLoading || pageQuery.isLoading) {
    return null
  }

  if (!cityQuery.data || !tagQuery.data) {
    return <ErrorPage />
  }

  if (!cityQuery.data.length || !tagQuery.data.length) {
    return <NotFoundPage />
  }

  const city = cityQuery.data[0]
  const tag = tagQuery.data[0]
  const page = pageQuery.data?.[0]

  return (
    <CityTagPage
      city={city}
      tag={tag}
      page={page}
    />
  )
}

function CityTagPage({ city, tag, page }) {

  const maxMiles = 50

  const areaFilter = {}
  if ((city.type === 'city' && city.major) || !city.location) {
    areaFilter.city_id = city.id
  } else {
    areaFilter.city_id_or_service_area = JSON.stringify({
      city_id: city.id,
      service_area: { ...city.location, radius: maxMiles }
    })
  }

  const catererQuery = useQuery(['caterers', {
    filter: {
      ...areaFilter,
      tag_id: tag.id,
      closed: false,
    },
    include: [
      'photo',
      'tags',
      'good_fors',
      'services',
    ],
    fields: {
      caterers: ['id', 'name', 'slug', 'description', 'short_description', 'ranking_tier'],
      photos: ['uuid', 'extension', 'path'],
      tags: ['name'],
      good_fors: ['name'],
      caterer_services: ['type']
    },
    sort: [
      '-ranking',
    ],
    page: {
      size: 21,
    }
  }])

  const navMenuItems = [
    {
      title: 'Why Work With Us',
      url: `#why-us`,
    },
    {
      title: 'How it Works',
      url: `#how-it-works`,
    },
    {
      title: 'Cost Calculator',
      url: `#cost`,
    }
  ]
  
  const { openModal, ...modalState } = useModalState()
  
  const tagNameSingular = pluralize.singular(tag.name)

  const precedingArticle = getPrecedingArticle(tag.slug)

  const calculatorHeading = `How Much Does ${tagNameSingular} Truck Catering Cost?`

  const faqTexts = getFaqTexts(tag.slug, city.name)

  const sectionFontColors = getSectionFontColors(tag.slug)

  let caterers = catererQuery.data && catererQuery.data.length ? [...catererQuery.data] : null

  const catererMetaTags = useMemo(() => {
    return JSON.stringify({
      '@context': 'https:\/\/schema.org',
      '@type': 'FoodService',
      'areaServed': {
        '@type': 'City',
        'name': city.name
      },
      'provider': caterers?.slice(0,2).map((caterer) => {
        return {
          '@type': 'Restaurant',
          url: `https:\/\/roaminghunger.com\/${caterer.slug}\/`,
          '@id': `https:\/\/roaminghunger.com\/${caterer.slug}\/#Restaurant`,
          name: caterer.name,
        }
      }),
      'description': `Experience food prepared in front of your eyes. Delicious ${tagNameSingular.toLocaleLowerCase()} catering from local caterers, food trucks, carts, pop-ups, and restaurants.`,
      'additionalType': [
        'https://www.wikidata.org/wiki/Q3303463',
        'https://www.wikidata.org/wiki/Q6887072'
      ],
      'image': [
        findImage(tag.photos, 'city-tag-section-1'),
        findImage(tag.photos, 'city-tag-how-it-works')
      ],
      'name': `${tagNameSingular} Catering in Los Angeles`,
      'url': `https://roaminghunger.com/catering/${city.slug}/${tag.slug}/`,
      '@id': `https://roaminghunger.com/catering/${city.slug}/${tag.slug}/#FoodService`
    })
  }, [caterers])

  if (catererQuery.isLoading) {
    return null
  }

  if (!catererQuery.data?.length) {
    return <NotFoundPage />
  }

  if (!city.major) {
    return <Redirect to={`/food-trucks/${city.url_slug}/${tag.slug}/`} />
  }

  // Skip indexing if the city is not a major city or market and there are fewer than 3 caterers
  const hasFewListings = !caterers || caterers?.length < 3
  const isCityMajorOrMarket = city.major || city.market
  const skipIndexing = (isCityMajorOrMarket && hasFewListings) || !isCityMajorOrMarket

  return (
    <Layout>
      <Helmet>
        <title>{page?.content.meta_title || `Top ${tagNameSingular} Caterers in ${city.name} - Roaming Hunger`}</title>
        <meta name="description" content={page?.content.meta_description || `Top ${tagNameSingular} Caterers in ${city.name}`}></meta>
        {page?.content.meta_keywords && <meta name="keywords" content={page.content.meta_keywords}></meta>}
        <meta name="og:title" content={page?.meta_title || `Top ${tagNameSingular} Caterers in ${city.name}`}></meta>
        <script type="application/ld+json">
          {catererMetaTags}
        </script>
        {skipIndexing ? (
          <meta name="robots" content="noindex" />
        ) : null}
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <ImageHero
        title={page?.content.heading || `${city.name} ${tagNameSingular} Truck Catering`}
        description={page?.content.description || tag.description}
        imageUrl={tag.photos[0] ? getResizedPhotoUrl(tag.photos[0]) : nachos}
        onButtonClick={openModal}
        style={{
          height: '80vh'
        }}
      />
      <NavbarFixed
        bgColor="bg-blue"
        className="text-white font-normal text-md"
        buttonStyle="text-white border-white border-2"
        menuItems={navMenuItems}
        onButtonClick={openModal}
      />
      {caterers && (
        <div className="bg-gray-200">
          <div className="container pt-16 pb-8 lg:pb-10">
            <h2 className="font-platform text-navy text-4xl md:text-5xl leading-none text-center">
              Find the Best {tagNameSingular} Truck Catering in {city.name}
            </h2>
          </div>
          <CatererCarousel
            caterers={caterers}
          />
          <div className="flex justify-center pt-8 pb-16">
            <button
              children="Get Started"
              className="btn btn-green font-platform px-10 py-3 text-2xl"
              onClick={openModal}
            />
          </div>
        </div>
      )}
      {shouldShowExtraSections(tag.slug) && (
        <div>
          <ImageSection
            heading="Save Time"
            description={`Compare all the ${tagNameSingular} trucks and caterers in ${city.name} in
             one place. We negotiate the best rate, coordinate logistics, and make sure the 
             truck shows up with everything you need. That gives you more time to 
             salivate over which toppings and sides you want.`}
            onButtonClick={openModal}
            className="mb-0"
            sectionFontClass={sectionFontColors[0]}
            imageUrl={findImage(tag.photos, 'city-tag-section-1')}

          />
          <ImageSection
            heading="Make Everyone Happy"
            description={getCustomText('makeHappy', tag.slug)}
            onButtonClick={openModal}
            sectionFontClass={sectionFontColors[1]}
            imageUrl={findImage(tag.photos, 'city-tag-section-2')}
          />
        </div>
      )}
      <WhyWorkWithUs
        id="why-us"
        subheading={`Hire a ${tagNameSingular.toLowerCase()} truck? You’re protected by the 100% Roaming Hunger Service Guarantee.`}
        onButtonClick={openModal}
      />
      {shouldShowExtraSections(tag.slug) && (
        <div>
          <ImageSection
            heading={`Relax & Enjoy the ${tag.name}`}
            description={getCustomText('relaxEnjoy', tag.slug)}
            onButtonClick={openModal}
            sectionFontClass={sectionFontColors[2]}
            imageUrl={findImage(tag.photos, 'city-tag-section-3')}
          />
          <ImageSection
            heading="RepEAT, RepEAT"
            description={getCustomText('repeatRepeat', tag.slug)}
            onButtonClick={openModal}
            sectionFontClass={sectionFontColors[3]}
            imageUrl={findImage(tag.photos, 'city-tag-section-4')}
          />
        </div>
      )}

      <CateringForAnyEvent
        className="bg-white"
        heading={`${tagNameSingular} Food Truck Catering For Any Event In ${city.name}`}
      />
      <HowItWorksLarge
        id="how-it-works"
        showMobile={true}
        image={findImage(tag.photos, 'city-tag-how-it-works') || howItWorksLarge}
        onButtonClick={openModal}
      />
      <Calculator
        id="cost"
        heading={calculatorHeading}
        onButtonClick={openModal}
      />
      <FeaturedClients
        onButtonClick={openModal}
      />
      <TagList
        place={city}
        heading={`Explore other ${city.name} Catering Options`}
        baseUrl={`/catering/${city.url_slug}`}
        darkMode={false}
      />
      {/*  REMOVING as duplicative content (hurting our SEO scores) 1/31/2024
        <section id="faq" className="bg-navy py-12 text-white">
          <div className="container max-w-4xl m-auto">
            <h2 className="text-3xl text-center font-platform mb-6">
              Frequently Asked Questions
            </h2>
            <h3 className="text-2xl font-platform mb-4">
              How much does it cost to rent {precedingArticle} {tagNameSingular} truck in {city.name}?
            </h3>
            {faqTexts.map((text, i) => (
              <p key={i} className="font-light mb-4 text-xl md:text-lg leading-relaxed mb-4">
                {text}
              </p>
            ))}
            <h3 className="text-2xl font-platform mb-4">
              How do I get pricing, availability, and catering packages for {tagNameSingular} food trucks near me?
            </h3>
            <p className="font-light mb-4 text-xl md:text-lg leading-relaxed mb-4">
              If your event details are confirmed and your budget meets the minimum (see above), you can get catering
                packages and quotes by submitting a catering request. Click any of the Get Started buttons on this page.
            </p>
            <h3 className="text-2xl font-platform mb-4">
              Why should I use Roaming Hunger?
            </h3>
            <p className="font-light mb-4 text-xl md:text-lg leading-relaxed mb-4">
              Roaming Hunger is the largest website for food trucks and mobile food businesses. Since 2009, we’ve helped thousands of companies and individuals book food trucks for catering and events. Today, we have over 20,500 independent food trucks, trailers, carts, and pop-ups on our site.
            </p>
            <p className="font-light mb-4 text-xl md:text-lg leading-relaxed mb-4">
              <strong>Variety</strong> - Our network consists of hundreds of local vendors in every major city, totaling 20,500+ nationally.
            </p>
            <p className="font-light mb-4 text-xl md:text-lg leading-relaxed mb-4">
              <strong>Save Time</strong> - Why have 100 conversations to find one food truck when you can have one conversation to find 100 food trucks?!
            </p>
            <p className="font-light mb-4 text-xl md:text-lg leading-relaxed mb-4">
              <strong>Speed</strong> - Whether you are ready to book one day or one month out from your event, we will provide every available food truck option.
            </p>
            <p className="font-light mb-4 text-xl md:text-lg leading-relaxed mb-4">
              <strong>Quality Options</strong>- All the food trucks we work with are gourmet, vetted, licensed, proven and reliable.
            </p>
            <p className="font-light mb-4 text-xl md:text-lg leading-relaxed mb-4">
              <strong>Service</strong> - Every catering is protected by our 100% Roaming Hunger Service Guarantee. If the caterer does not show, we’ll get you a replacement caterer or refund 100% of your money.
            </p>
          </div>
        </section>
      */}
    </Layout>
  )
}
